import React from 'react';
import { useTranslation } from 'react-i18next';

import config from '../../client/config';
import AboutCampaignAndCharitySection from '../../components/AboutCampaignAndCharitySection/AboutCampaignAndCharitySection';
import { Banner } from '../../components/Banner/Banner';
import CampaignLogo from '../../components/CampaignLogo/CampaignLogo';
import CampaignStartFundraising from '../../components/CampaignStartFundraising/CampaignStartFundraising';
import CharityRegNumber from '../../components/CharityRegNumber/CharityRegNumber';
import ClosingDate from '../../components/ClosingDate/ClosingDate';
import DonateButton from '../../components/DonateButton/DonateButton';
import DonationSummary from '../../components/DonationSummary/DonationSummary';
import Editbar from '../../components/Editbar/Editbar';
import EventDetails from '../../components/EventDetails/EventDetails';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import FundraisersList from '../../components/FundraisersList/FundraisersList';
import { GoToImmersiveGiving } from '../../components/GoToImmersiveGiving/GoToImmersiveGiving';
import CampaignsHelmet from '../../components/Helmet/CampaignsHelmet';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import DefaultLayout from '../../components/Layout/DefaultLayout';
import Page from '../../components/Page/Page';
import PageAlert from '../../components/PageAlert/PageAlert';
import PageOwnerTitle from '../../components/PageOwnerTitle/PageOwnerTitle';
import PageSection from '../../components/PageSection/PageSection';
import QRModal from '../../components/QRModal/QRModal';
import { Share } from '../../components/SocialButtons/Share';
import SocialButtonsListWrapper from '../../components/SocialButtons/SocialButtonsListWrapper';
import StickySummary from '../../components/StickySummary/StickySummary';
import Story from '../../components/Story/Story';
import SuccessMessage from '../../components/SuccessMessage/SuccessMessage';
import SupportersList from '../../components/SupportersList/SupportersList';
import TeamsList from '../../components/TeamsList/TeamsList';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import { Totals } from '../../components/Totals/Totals';
import WeDidItHeader from '../../components/WeDidItHeader/WeDidItHeader';
import { Experiments } from '../../constants/experiments';
import { PageMetaContext } from '../../context/PageMetaContext';
import { MessageOverrides } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useFlags } from '../../hooks/useFlags';
import { useInView } from '../../hooks/useinView';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { isCharity } from '../../utils/typeGuards';

import '../Pages.scss';
import styles from './CampaignPage.scss';

export const CampaignPage: React.FunctionComponent = () => {
  const { inViewRef, inView, rootIsBelowTarget } = useInView();
  const basePageData = useBasePageQuery();
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName } = useNotNullableContext(PageMetaContext);

  const { enableOtgImmersiveGiving } = useFlags();

  const data = basePageData?.data?.page;
  const legacyId = data && data.legacyId;
  const shareSubject = '';
  const shareMessage = '';
  const isOTGImmersiveGivingShortname =
    pageShortName === 'make-a-refugee-shelter-a-home';

  //B2C_030 - Generic share button on campaign pages
  // Keep only if group B wins the experiment
  const charities = data?.relationships?.beneficiaries.nodes.filter(isCharity);
  const charityName = charities?.length ? charities[0].name : null;
  const charityCountryCode = charities?.length
    ? charities[0].countryCode
    : null;
  const isNonUKCharity = charityCountryCode !== 'GB';
  const emailBodyKey = isNonUKCharity
    ? 'campaign.share.nonUK_emailBody'
    : 'campaign.share.emailBody';
  const shareMessageOverrides: MessageOverrides = {
    email: emailUrl => {
      return {
        body: t(emailBodyKey, { charityName, emailUrl }),
        subject: t('common.defaultEmailSubject'),
      };
    },
    sms: pageUrl =>
      t('campaign.share.whatsAppAndSms', { charityName, pageUrl }),
    whatsApp: pageUrl =>
      t('campaign.share.whatsAppAndSms', { charityName, pageUrl }),
    twitter: () => t('campaign.share.twitter', { charityName }),
  };
  return (
    <Page>
      <IsQRCode>
        <QRModal ownerName={data?.title} />
      </IsQRCode>
      <CampaignsHelmet />
      <PageAlert />
      <Banner />
      <Editbar
        adminLink={`${config.baseUri}/campaign/manage/edit/${legacyId}`}
      />
      <SuccessMessage />
      <WeDidItHeader />
      <DefaultLayout
        slots={{
          featureImage: <FeatureImage enableLightbox />,
          ownerAvatar: <CampaignLogo />,
          titles: <PageOwnerTitle />,
          totals: (
            <>
              <TotaliserWrapper />
              <Totals />
            </>
          ),
          callsToAction: (
            <div ref={inViewRef} className={styles.ctaWrapper}>
              <DonateButton
                className="jg-space-mrsm jg-space-mrn@lg jg-space-mbsm"
                successType="StaticDonateButtonClick"
              />
              <Share
                successType="StaticShareButtonClick"
                messageOverrides={shareMessageOverrides}
              />
            </div>
          ),
          titlesSupplementary: (
            <>
              <ClosingDate />
              <EventDetails />
              <CharityRegNumber />
            </>
          ),
          causeMini:
            isOTGImmersiveGivingShortname &&
            enableOtgImmersiveGiving &&
            legacyId ? (
              <GoToImmersiveGiving />
            ) : (
              <CampaignStartFundraising />
            ),
          main: (
            <>
              <PageSection title={t('story.title')} hasBorder>
                <Story />
                <h3 className="jg-text--center">
                  {t('story.shareTitle', 'Share this story')}
                </h3>
                <SocialButtonsListWrapper
                  subject={shareSubject}
                  message={shareMessage}
                  suppressGiftAidMessage={isNonUKCharity}
                />
              </PageSection>
            </>
          ),
          aside: (
            <>
              <FundraisersList />
              <TeamsList />
              <SupportersList
                noSupportersMessage={t(
                  'campaign.noSupportersMessage',
                  'Try making a donation to get things going',
                )}
              />
            </>
          ),
          contentFooterPrimary: (
            <AboutCampaignAndCharitySection withExternalUrl={true} />
          ),
          contentFooterSecondary: (
            <DonationSummary
              fieldsToShow={[
                'directDonationAmount',
                'fundraiserRaisedAmount',
                'offlineAmount',
              ]}
            />
          ),
        }}
      />
      {!inView && rootIsBelowTarget && (
        <StickySummary shareMessageOverrides={shareMessageOverrides} />
      )}
    </Page>
  );
};

CampaignPage.displayName = 'CampaignPage';

export default CampaignPage;
